import { useTranslation } from 'react-i18next';
import LabeledInput from '@/pages/newSupported/components/LabeledInput';
import Toggle from '@/pages/newSupported/components/Toggle';
import FileUpload from '@/pages/newSupported/components/FileUpload';
import { ChildDtoOut } from '@/models/out/ChildDtoOut';
import addIcon from '@/assets/icons/add.svg';
import minusIcon from '@/assets/icons/minus.svg';
import closeIcon from '@/assets/icons/close.svg';
import { MIN_BIRTH_DATE_INPUT } from '@/helpers/dateHelper';
import { toHebrewName } from '@/helpers/hebrewName';

interface AddChildrenFormProps {
  children: ChildDtoOut[];
  onChildrenChange: (children: ChildDtoOut[]) => void;
  documents: File[];
  onDocumentsChange: (documents: File[]) => void;
}

const AddChildrenForm = ({ children, onChildrenChange, documents, onDocumentsChange }: AddChildrenFormProps) => {
  const { t } = useTranslation();

  const handleAddChild = () => {
    onChildrenChange([
      ...children,
      {
        firstName: '',
        israeliId: '',
        birthDate: undefined,
        livesWithSupported: true,
      },
    ]);
  };

  const handleRemoveChild = (index: number) => {
    onChildrenChange(children.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index: number, field: keyof ChildDtoOut, value: any) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    onChildrenChange(updated);
  };

  const handleFileAdd = (file: File | null) => {
    if (!file) return;
    onDocumentsChange([...documents, file]);
  };

  const handleFileRemove = (fileIndex: number) => {
    onDocumentsChange(documents.filter((_, i) => i !== fileIndex));
  };

  const renderChildFields = (child: ChildDtoOut, index: number) => (
    <div key={index} className="flex flex-col gap-4">
      {index > 0 && (
        <div className="bg-(--surface-grey-lowest) border border-(--border-lowest) rounded-2xl p-8 max-[1099px]:p-4 relative shadow-sm">
          <div className="flex flex-col gap-6">
            <div className="flex gap-8 max-[1099px]:flex-col max-[1099px]:gap-4">
              <LabeledInput
                label={t('firstName')}
                id={`childFirstName-${index}`}
                value={child.firstName}
                onChange={(e) => handleFieldChange(index, 'firstName', toHebrewName(e.target.value))}
                required
                width="270px"
                mobileFullWidth
              />
              <LabeledInput
                label={t('israeliId')}
                id={`childId-${index}`}
                value={child.israeliId}
                onChange={(e) => handleFieldChange(index, 'israeliId', e.target.value)}
                required
                width="270px"
                mobileFullWidth
              />
              <LabeledInput
                label={t('birthDate')}
                id={`childBirthDate-${index}`}
                type="date"
                min={MIN_BIRTH_DATE_INPUT}
                value={child.birthDate ? child.birthDate.toISOString().split('T')[0] : ''}
                onChange={(e) => handleFieldChange(index, 'birthDate', e.target.value ? new Date(e.target.value) : undefined)}
                required
                width="270px"
                mobileFullWidth
              />
            </div>
            <div className="flex gap-8 items-start">
              <Toggle
                label={t('livesWithSupportedQuestion')}
                value={child.livesWithSupported}
                onChange={(value) => handleFieldChange(index, 'livesWithSupported', value)}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleRemoveChild(index)}
            className="absolute start-[-12px] top-6 w-6 h-6 bg-(--font-error) rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
            title={t('removeChild')}
          >
            <img src={minusIcon} alt="" />
          </button>
        </div>
      )}

      {index === 0 && (
        <div className="flex flex-col gap-6">
          <div className="flex gap-8 max-[1099px]:flex-col max-[1099px]:gap-4">
            <LabeledInput
              label={t('firstName')}
              id={`childFirstName-${index}`}
              value={child.firstName}
              onChange={(e) => handleFieldChange(index, 'firstName', toHebrewName(e.target.value))}
              required
              width="270px"
              mobileFullWidth
            />
            <LabeledInput
              label={t('israeliId')}
              id={`childId-${index}`}
              value={child.israeliId}
              onChange={(e) => handleFieldChange(index, 'israeliId', e.target.value)}
              required
              width="270px"
              mobileFullWidth
            />
            <LabeledInput
              label={t('birthDate')}
              id={`childBirthDate-${index}`}
              type="date"
              min={MIN_BIRTH_DATE_INPUT}
              value={child.birthDate ? child.birthDate.toISOString().split('T')[0] : ''}
              onChange={(e) => handleFieldChange(index, 'birthDate', e.target.value ? new Date(e.target.value) : undefined)}
              required
              width="270px"
              mobileFullWidth
            />
          </div>
          <div className="flex gap-8 items-start">
            <Toggle
              label={t('livesWithSupportedQuestion')}
              value={child.livesWithSupported}
              onChange={(value) => handleFieldChange(index, 'livesWithSupported', value)}
            />
          </div>
        </div>
      )}
    </div>
  );

  if (children.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('addNewChildren')}</h4>
        <button
          type="button"
          onClick={handleAddChild}
          className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
        >
          <div className="w-[10px] h-[10px] relative">
            <img src={addIcon} alt="" />
          </div>
          {t('addChild')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('addNewChildren')}</h4>

      {children.map((child, index) => renderChildFields(child, index))}

      <button
        type="button"
        onClick={handleAddChild}
        className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
      >
        <div className="w-[10px] h-[10px] relative">
          <img src={addIcon} alt="" />
        </div>
        {t('addAnotherChild')}
      </button>

      {/* Documents Section - For All Children */}
      {children.length > 0 && (
        <div className="flex flex-col gap-4">
          <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('documents')}</h4>
          <div style={{ width: '846px', maxWidth: '100%' }}>
            <FileUpload placeholder={t('attachChildDocument')} value={null} onChange={handleFileAdd} accept="image/*,.pdf" />
          </div>
          {documents.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="body-sm-regular text-(--font-body-2)">{t('uploadedDocuments')}:</span>
              <div className="flex flex-wrap gap-2">
                {documents.map((doc, docIndex) => (
                  <div key={docIndex} className="bg-(--surface-base) border border-(--border-strong) rounded h-8 px-3 flex items-center gap-2">
                    <button type="button" onClick={() => handleFileRemove(docIndex)} className="w-[10px] h-[10px] flex items-center justify-center">
                      <img src={closeIcon} width={15} height={15} alt="" />
                    </button>
                    <div className="w-0 h-[10px] border-r border-(--border-main)" />
                    <span className="body-sm-regular text-(--font-body-5)">{doc.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddChildrenForm;
